// DraftsManager — full-screen modal that lists the current cashier's
// in-flight transaction drafts. Lets them load a draft back into the
// cart (resume) or delete it. Strictly per-cashier via the `useMyDrafts`
// query and the server-side rule on `transactionDrafts/{id}`.

import { useEffect, useState } from 'react';
import { Inbox, Trash2, RotateCcw, Clock, ShoppingCart } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Chip } from '@/components/ui/Chip';
import { EmptyState } from '@/components/ui/EmptyState';
import { LoadingState } from '@/components/ui/LoadingState';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { useMyDrafts, useDeleteDraft } from '@/hooks/useDrafts';
import { cn, formatIDR, formatNumber, formatDateTime, formatRelative } from '@/lib/format';
import type { TransactionDraft } from '@/types/domain';

export interface DraftsManagerProps {
  open: boolean;
  onClose: () => void;
  cashierId: string;
  /**
   * Called when the cashier picks a draft to load. The caller is
   * responsible for replacing the cart, customer, discount, tax, and
   * fulfillment with the draft's state. If `currentCartItemCount > 0`,
   * the caller should confirm before overwriting (we surface a confirm
   * here so the behaviour is consistent).
   */
  onLoad: (draft: TransactionDraft) => void;
  /** Number of items currently in the cart — used to confirm before clobbering. */
  currentCartItemCount: number;
}

export function DraftsManager({
  open,
  onClose,
  cashierId,
  onLoad,
  currentCartItemCount,
}: DraftsManagerProps) {
  const { data: drafts, isLoading } = useMyDrafts(open ? cashierId : null);
  const deleteDraft = useDeleteDraft();
  const [pendingDelete, setPendingDelete] = useState<TransactionDraft | null>(null);
  const [pendingLoad, setPendingLoad] = useState<TransactionDraft | null>(null);

  // Reset local state when modal closes
  useEffect(() => {
    if (!open) {
      setPendingDelete(null);
      setPendingLoad(null);
    }
  }, [open]);

  const handleLoadClick = (d: TransactionDraft) => {
    if (currentCartItemCount > 0) {
      setPendingLoad(d);
      return;
    }
    onLoad(d);
  };

  const handleConfirmLoad = () => {
    if (!pendingLoad) return;
    onLoad(pendingLoad);
    setPendingLoad(null);
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await deleteDraft.mutateAsync({ id: pendingDelete.id, cashierId });
    } finally {
      setPendingDelete(null);
    }
  };

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        title="Draft transaksi"
        description="Cart yang disimpan sementara — hanya kamu yang bisa lihat."
        size="lg"
        footer={
          <Button variant="ghost" onClick={onClose}>
            Tutup
          </Button>
        }
      >
        {isLoading ? (
          <LoadingState message="Memuat draft…" />
        ) : !drafts || drafts.length === 0 ? (
          <EmptyState
            icon={<Inbox size={24} strokeWidth={1.5} />}
            title="Belum ada draft"
            description="Kalau ada cart yang mau diparkir sementara, klik 'Simpan draft' di keranjang. Draft cuma tersimpan untuk akun kamu."
          />
        ) : (
          <ul className="space-y-2 max-h-[60vh] overflow-y-auto overscroll-contain pr-1">
            {drafts.map((d) => (
              <DraftRow
                key={d.id}
                draft={d}
                onLoad={() => handleLoadClick(d)}
                onDelete={() => setPendingDelete(d)}
                loading={deleteDraft.isPending}
              />
            ))}
          </ul>
        )}
      </Modal>

      <ConfirmDialog
        open={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        title="Hapus draft?"
        description={
          pendingDelete
            ? `Draft "${pendingDelete.label}" akan dihapus permanen. Cart yang sekarang tidak akan berubah.`
            : ''
        }
        confirmText="Hapus"
        variant="danger"
        loading={deleteDraft.isPending}
        onConfirm={handleConfirmDelete}
      />

      <ConfirmDialog
        open={!!pendingLoad}
        onClose={() => setPendingLoad(null)}
        title="Ganti isi keranjang?"
        description={
          pendingLoad
            ? `Keranjang kamu sekarang ada ${formatNumber(currentCartItemCount)} item. Memuat draft akan menggantikannya.`
            : ''
        }
        confirmText="Ganti"
        variant="primary"
        onConfirm={handleConfirmLoad}
      />
    </>
  );
}

function DraftRow({
  draft,
  onLoad,
  onDelete,
  loading,
}: {
  draft: TransactionDraft;
  onLoad: () => void;
  onDelete: () => void;
  loading: boolean;
}) {
  const itemCount = draft.items.reduce((s, i) => s + i.qty, 0);
  return (
    <li>
      <Card
        padding="md"
        className={cn(
          'flex items-start gap-3',
          'hover:border-primary/40 transition-colors',
        )}
      >
        <div className="w-9 h-9 rounded bg-primary/10 text-primary flex items-center justify-center shrink-0">
          <ShoppingCart size={16} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-body font-medium text-ink truncate">
              {draft.label || 'Draft tanpa nama'}
            </p>
            {draft.customerSnapshot?.name ? (
              <Chip variant="info">{draft.customerSnapshot.name}</Chip>
            ) : null}
            {draft.fulfillment === 'later' ? (
              <Chip variant="warning">Pending</Chip>
            ) : (
              <Chip variant="success">Ambil sekarang</Chip>
            )}
          </div>

          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-caption text-ink-subtle">
            <span>
              {formatNumber(itemCount)} item · {draft.items.length} baris
            </span>
            <span aria-hidden>·</span>
            <span>{formatIDR(draft.grandTotal)}</span>
            <span aria-hidden>·</span>
            <span className="inline-flex items-center gap-1">
              <Clock size={11} />
              {formatRelative(draft.updatedAt)} ({formatDateTime(draft.updatedAt)})
            </span>
          </div>

          {draft.eventName && (
            <p className="text-caption text-ink-subtle mt-1 truncate">
              Event: {draft.eventName}{' '}
              <code className="font-mono">{draft.eventCode}</code>
            </p>
          )}
        </div>

        <div className="flex flex-col items-end gap-1.5 shrink-0">
          <Button
            variant="primary"
            size="sm"
            onClick={onLoad}
            leftIcon={<RotateCcw size={12} />}
            disabled={loading}
          >
            Muat
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onDelete}
            disabled={loading}
            leftIcon={<Trash2 size={12} />}
            className="text-danger hover:text-danger"
          >
            Hapus
          </Button>
        </div>
      </Card>
    </li>
  );
}
